import { useEffect, useState, type AnchorHTMLAttributes, type MouseEvent } from "react";

/**
 * Path routing (replaces the old hash routes) so deep links, invitations and the Amplify SPA
 * rewrite behave. History API only; no dependency.
 */
export const TRIP_SECTIONS = [
  "overview",
  "sync",
  "sources",
  // Where the trip's places and categories are managed. Reached from Settings, never listed in
  // the nav, like Sources.
  "places",
  "map",
  "media",
  // The Media Library's other half: folders of tags. A tag with a note on it is an idea,
  // and filing several recordings under one is what used to be a collection — so this one
  // section replaced both.
  "tags",
  "deliverables",
  "people",
  "storage",
  "activity",
  "settings",
] as const;
export type TripSection = (typeof TRIP_SECTIONS)[number];

export const DEFAULT_SECTION: TripSection = "overview";

/**
 * Where a trip opens. Overview is the desktop home and is not in the phone's bottom bar, so a
 * narrow viewport opens on Sync instead — the traveller's home, and the step the rest depends on.
 * Width, not `isNative()`: the bottom bar is width-driven, so the landing has to match that axis
 * or a phone browser lands on a section its bar cannot show.
 */
export function landingSection(roles: string[] = []): TripSection {
  // An editor has neither of the usual homes: no Sync, because nothing of theirs is uploaded, and
  // no Overview, because the trip's capture and sharing are not theirs to watch. The map is both
  // their home and the surface they review from, on a phone as on a desktop.
  if (roles.includes("EDITOR") && !roles.includes("ADMIN")) return "map";
  const narrow =
    typeof window !== "undefined" && window.matchMedia?.("(max-width: 639px)").matches === true;
  return narrow ? "sync" : "overview";
}

export type Route =
  | { name: "trips" }
  | { name: "newTrip" }
  | { name: "trip"; tripId: string; section: TripSection; itemId: string | null }
  | { name: "invite"; token: string | null }
  | { name: "notFound" };

const UUID = "[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}";
// An optional third segment addresses one thing inside a section — today, one tag's folder —
// so a folder can be linked to and the back button leaves it. `likes` is named here rather
// than left to a catch-all: it is the one folder with no id, it is always on the wall, and a
// regex that only took UUIDs made it 404 for everybody. It stands where `untagged` stood,
// which is no longer an address at all.
export const LIKES = "likes";
// Likewise the Places page's Categories tab, so the map's pencil can open the page on it.
export const CATEGORIES_TAB = "categories";
/**
 * A place Google found, on its way to the Places page to be added: `google-<place id>` in the
 * same third segment. Google's ids are neither UUIDs nor one of the page's own words, so the
 * prefix is what keeps the three apart. An id and nothing else travels, so the address says
 * nothing about the place itself, and the page asks Google what it is on arrival.
 */
const GOOGLE = "google-";
export const googleSegment = (placeId: string) => `${GOOGLE}${placeId}`;
/** The place id an address names, or null where it names anything else. */
export const googlePlaceId = (itemId: string | null) =>
  itemId !== null && itemId.startsWith(GOOGLE) ? itemId.slice(GOOGLE.length) || null : null;
const TRIP_RE = new RegExp(
  `^/trips/(${UUID})(?:/([a-z]+))?(?:/(${UUID}|${LIKES}|${CATEGORIES_TAB}|${GOOGLE}[A-Za-z0-9_-]+))?/?$`,
);

export function matchRoute(pathname: string, hash = ""): Route {
  if (pathname === "/" || pathname === "") return { name: "trips" };
  if (pathname === "/trips/new") return { name: "newTrip" };
  if (pathname === "/invite") {
    const token = /^#token=([A-Za-z0-9_-]+)$/.exec(hash)?.[1] ?? null;
    return { name: "invite", token };
  }
  const m = TRIP_RE.exec(pathname);
  if (m) {
    const section = (m[2] ?? DEFAULT_SECTION) as TripSection;
    if ((TRIP_SECTIONS as readonly string[]).includes(section)) {
      return { name: "trip", tripId: m[1]!, section, itemId: m[3] ?? null };
    }
  }
  return { name: "notFound" };
}

export const tripPath = (tripId: string, section: TripSection = DEFAULT_SECTION, itemId?: string) =>
  `/trips/${tripId}/${section}${itemId ? `/${itemId}` : ""}`;

const LEGACY_TABS: Record<string, TripSection> = {
  library: "media",
  // Collections and ideas are both just tags now, so their old links land on the Tags tab
  // rather than on nothing.
  collections: "tags",
  ideas: "tags",
  production: "deliverables",
};

/** Old `#/trips/<id>/<tab>` links (already shared) map to their new paths. */
export function legacyHashToPath(hash: string): string | null {
  if (hash === "#/" || hash === "#") return "/";
  const m = new RegExp(`^#/trips/(${UUID})(?:/([a-z]+))?$`).exec(hash);
  if (!m) return null;
  return tripPath(m[1]!, LEGACY_TABS[m[2] ?? "library"] ?? "media");
}

const listeners = new Set<() => void>();

export function navigate(to: string, opts: { replace?: boolean } = {}) {
  if (opts.replace) window.history.replaceState(null, "", to);
  else window.history.pushState(null, "", to);
  listeners.forEach((l) => l());
  window.scrollTo?.(0, 0);
}

export function useRoute(): Route {
  const read = () => matchRoute(window.location.pathname, window.location.hash);
  const [route, setRoute] = useState<Route>(() => {
    const legacy = legacyHashToPath(window.location.hash);
    if (legacy) window.history.replaceState(null, "", legacy);
    return read();
  });
  useEffect(() => {
    const on = () => {
      // An old `#/…` link followed while the app is already open arrives as a hash change.
      const legacy = legacyHashToPath(window.location.hash);
      if (legacy) window.history.replaceState(null, "", legacy);
      setRoute(read());
    };
    listeners.add(on);
    window.addEventListener("popstate", on);
    window.addEventListener("hashchange", on);
    return () => {
      listeners.delete(on);
      window.removeEventListener("popstate", on);
      window.removeEventListener("hashchange", on);
    };
  }, []);
  return route;
}

/** An anchor that navigates in-app for plain left clicks and stays a real link otherwise. */
export function Link({
  to,
  onClick,
  ...rest
}: AnchorHTMLAttributes<HTMLAnchorElement> & { to: string }) {
  return (
    <a
      href={to}
      onClick={(e: MouseEvent<HTMLAnchorElement>) => {
        onClick?.(e);
        if (
          e.defaultPrevented ||
          e.button !== 0 ||
          e.metaKey ||
          e.ctrlKey ||
          e.shiftKey ||
          e.altKey
        )
          return;
        e.preventDefault();
        navigate(to);
      }}
      {...rest}
    />
  );
}
